import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { type TruncateProps } from './types';
import { cn } from '../../utils/cn';
import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip';

/**
 * A text component that truncates overflowing content with an ellipsis and optionally shows
 * the full text in a tooltip on hover.
 *
 * The text can be provided either via the `text` prop or as `children`.
 * If both are provided, `text` takes precedence.
 *
 * The tooltip will only appear when the text is actually truncated (i.e., when it overflows).
 * If the text fits within the container, no tooltip is shown even if `showTooltip` is true.
 *
 * @param text - The text content to truncate
 * @param children - Alternative way to pass text content
 * @param maxWidth - Maximum width of the text container (number for pixels, or CSS string)
 * @param showTooltip - Whether to show a tooltip with the full text on hover. Defaults to true.
 * @param className - Additional CSS classes
 *
 * @example
 * <Truncate text="This is a very long text that will be truncated" maxWidth={200} />
 *
 * @example
 * <Truncate maxWidth="150px">
 *   This is a very long text that will be truncated
 * </Truncate>
 *
 * @example
 * <Truncate maxWidth="50%" showTooltip={false}>Truncation without tooltip</Truncate>
 */
export const Truncate: React.FC<TruncateProps> = ({
    children,
    className,
    maxWidth,
    showTooltip = true,
    text,
    ...props
}) => {
    const content = text ?? children;
    const elementRef = useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const checkTruncation = useCallback(() => {
        const element = elementRef.current;
        if (element) {
            // Allow 1px of slack: subpixel rounding often makes scrollWidth exceed
            // clientWidth by a fraction of a pixel even when nothing is clipped.
            setIsTruncated(element.scrollWidth - element.clientWidth > 1);
        }
    }, []);

    useLayoutEffect(() => {
        checkTruncation();

        const element = elementRef.current;
        if (element && typeof ResizeObserver !== 'undefined') {
            const resizeObserver = new ResizeObserver(checkTruncation);
            resizeObserver.observe(element);

            return () => resizeObserver.disconnect();
        }

        window.addEventListener('resize', checkTruncation);
        return () => {
            window.removeEventListener('resize', checkTruncation);
        };
    }, [checkTruncation, content, isTruncated, maxWidth]);

    const maxWidthStyle = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;

    const truncatedElement = (
        <span
            className={cn('block truncate', className)}
            data-testid="truncate"
            ref={elementRef}
            style={{ maxWidth: maxWidthStyle }}
            {...props}
        >
            {content}
        </span>
    );

    if (!showTooltip || !isTruncated) {
        return truncatedElement;
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>{truncatedElement}</TooltipTrigger>
            <TooltipContent>{content}</TooltipContent>
        </Tooltip>
    );
};

Truncate.displayName = 'Truncate';
